import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil, startCase } from 'lodash-es';

import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { Project } from '~hooks/useProjects/models';
import { WayPoint } from '~hooks/useSites/models';
import UserStore from '~store/UserStore';
import { Nullable } from '~types/Nullable';

import formatNumber from './formatNumber';
import { getEffectiveUserCompanyId } from './user/user-utils';
import { parseFiniteNumber } from './utilFunctions';

export function extractWaypointData(waypoint?: WayPoint) {
  if (!waypoint?.siteNested) {
    return null;
  }

  const name = waypoint.siteNested.name;
  const address = waypoint.siteNested.address?.thoroughfare;
  const fullAddress = waypoint.site?.fullAddress;
  const geofenceType = waypoint.siteNested.nextBillionGeofence?.geofenceType;

  return { name, address, fullAddress, geofenceType };
}

export function makeOrderNameWithDropOffFallback(order: Order) {
  return order.name || extractWaypointData(order.dropOffWayPoints?.[0])?.name;
}

export function makeOrderTitle(order: Order, prependWithOrderId: boolean = false) {
  const fragments: string[] = [];
  const orderName = makeOrderNameWithDropOffFallback(order);

  if (prependWithOrderId) {
    fragments.push(order.orderId);
  }

  if (order.dispatchNumber?.name) {
    fragments.push(order.dispatchNumber.name);
  }

  if (orderName) {
    fragments.push(orderName);
  }

  return fragments.join(' • ');
}

// The dispatch card's OrderCardSubtitle renders the order's "sending" (customer)
// accounts as a single comma-joined string, excluding any account whose name
// duplicates the project account (`order.account`), and wraps it in
// <HighlightedText />. Centralize that exact filter + join here so the on-card
// highlight and the omni-search "why matched" derivation (orderSearchMatches)
// mirror ONE string by construction and cannot drift apart (TRE-15532).
export function makeOrderSenderDisplay(order: Order) {
  return order.sendingAccounts
    .filter((account) => account.name !== order.account?.name)
    .map((account) => account.name)
    .join(', ');
}

export function userCompanyOwnsOrder(userStore: UserStore, order: Order) {
  const userCompanyId = getEffectiveUserCompanyId(userStore);

  return userCompanyId === order.company?.id;
}

type RateTarget = 'customer' | 'haul' | 'pay' | 'driver';

type RateBearer = {
  customerRate?: { type: string; rate: number | null } | null;
  customerRateType?: string | null;
  customerRateValue?: number | null;
  vendorRate?: { type: string; rate: number | null } | null;
  vendorRateType?: string | null;
  vendorRateValue?: number | null;
  driverRate?: { type: string; rate: number | null } | null;
  driverRateType?: string | null;
  driverRateValue?: number | null;
};

function extractRateInfoFromRateBearer(
  source: RateBearer,
  target: RateTarget,
): { rateType: string; rateValue: number | null } | null {
  if (target === 'driver') {
    if (source.driverRate) {
      return { rateType: source.driverRate.type, rateValue: source.driverRate.rate };
    }

    if (source.driverRateType) {
      return {
        rateType: source.driverRateType,
        rateValue: source.driverRateValue ?? null,
      };
    }

    return null;
  }

  if (target === 'haul' || target === 'customer') {
    if (source.customerRate) {
      return {
        rateType: source.customerRate.type,
        rateValue: source.customerRate.rate,
      };
    }

    if (source.customerRateType) {
      return {
        rateType: source.customerRateType,
        rateValue: source.customerRateValue ?? null,
      };
    }

    return null;
  }

  if (source.vendorRate) {
    return { rateType: source.vendorRate.type, rateValue: source.vendorRate.rate };
  }

  if (source.vendorRateType) {
    return {
      rateType: source.vendorRateType,
      rateValue: source.vendorRateValue ?? null,
    };
  }

  return null;
}

export function extractRateInfoFromOrderOrProject(
  order: Order | Project,
  target: RateTarget,
): { rateType: string; rateValue: number | null } | null {
  return extractRateInfoFromRateBearer(order, target);
}

export function extractRateInfoFromDefaultSiteRateSchedule(
  order: Order,
  target: RateTarget,
): { rateType: string; rateValue: number | null } | null {
  const schedule = order.defaultSiteRateSchedule;

  if (!schedule) {
    return null;
  }

  return extractRateInfoFromRateBearer(schedule, target);
}

export function formatOrderStartTime(order: Order) {
  return order.loadAt ? dayjs.tz(order.loadAt).format('h:mm A ddd MMM-DD-YYYY') : '';
}

// While a trucks-count edit waits for the summary to catch up, the order's own count is
// the newer one.
export function getOrderTrucksProgress(
  order: Order,
  isWaitingForTrucksCountMatch = false,
) {
  const accepted = order.orderSummary?.acceptedJobsCount ?? 0;
  const total = isWaitingForTrucksCountMatch
    ? order.truckCount
    : (order.orderSummary?.jobsCount ?? order.truckCount);

  return {
    accepted,
    acceptedText: `${accepted} / ${total} ${t('dispatch.summary_counts.accepted')}`,
    total,
  };
}

// Jobs that were never fetched count as loading.
function isOrderCheckboxDisabled(
  isLoadingOrderJobs: Map<string, boolean>,
  orderJobs: Map<string, Job[]>,
  orderId: string,
  readOnly: boolean,
) {
  const orderHasLoadedJobs = (orderJobs.get(orderId)?.length ?? 0) > 0;

  return readOnly || (isLoadingOrderJobs.get(orderId) ?? true) || !orderHasLoadedJobs;
}

export function getOrderCheckedState(
  orderJobs: ReadonlyMap<string, Job[]>,
  orderId: string,
  checkedJobIds: string[] = [],
) {
  const jobsInOrder = orderJobs.get(orderId) ?? [];
  const checkedJobsInOrder = jobsInOrder.filter((job) => checkedJobIds.includes(job.id));

  if (checkedJobsInOrder.length === 0) {
    return false;
  }

  const allJobsChecked = checkedJobsInOrder.length === jobsInOrder.length;

  if (allJobsChecked) {
    return true;
  }

  return 'intermediate';
}

interface OrderCheckboxStateOptions {
  checkedJobIds?: string[];
  isLoadingOrderJobs: Map<string, boolean>;
  orderId: string;
  orderJobs: Map<string, Job[]>;
  readOnly: boolean;
}

export function getOrderCheckboxState({
  checkedJobIds,
  isLoadingOrderJobs,
  orderId,
  orderJobs,
  readOnly,
}: OrderCheckboxStateOptions) {
  const orderCheckedState = getOrderCheckedState(orderJobs, orderId, checkedJobIds);

  return {
    isChecked: orderCheckedState === true,
    isDisabled: isOrderCheckboxDisabled(isLoadingOrderJobs, orderJobs, orderId, readOnly),
    isIntermediate: orderCheckedState === 'intermediate',
  };
}

export function formatContactText(name: string, phone?: Nullable<string>) {
  return phone ? `${name} ${phone}` : name;
}

export function getOrderNotes(
  order: Order,
  field: 'internalNotes' | 'jobNotes' | 'orderNotes',
) {
  return order[field] || order.project?.[field] || undefined;
}

function appendUnit(quantityText: string, unitOfMeasureId?: string) {
  return unitOfMeasureId ? `${quantityText} ${startCase(unitOfMeasureId)}` : quantityText;
}

export function makeOrderedQuantityText(
  quantity: Nullable<string | number>,
  unitOfMeasureId?: string,
) {
  const orderedQuantity = Number(quantity ?? 0);

  if (!orderedQuantity) {
    return null;
  }

  return appendUnit(formatNumber(orderedQuantity), unitOfMeasureId);
}

export function makeOrderedMaterialText(order: Order) {
  if (!order.editable) {
    return '-';
  }

  if (order.materialTotals.length > 1) {
    return order.materialTotals
      .map((materialTotal) => {
        const orderedQuantity = parseFiniteNumber(materialTotal.totalQuantity);
        const quantityText = appendUnit(
          isNil(orderedQuantity) ? '-' : formatNumber(orderedQuantity),
          materialTotal.unitOfMeasure?.id,
        );

        return `${materialTotal.material?.name ?? '-'} ${quantityText}`;
      })
      .join(', ');
  }

  return (
    makeOrderedQuantityText(order.quantity, order.unitOfMeasure?.id) ??
    t('dispatch.dispatch_v3.delivered_quantities_widget.empty_top_row')
  );
}

function makeLoadsText(count: number) {
  return `${count} ${count > 1 ? t('common.loads') : t('common.load')}`;
}

export function getOrderDeliveredLoads(order: Order) {
  const { jobQuantity, jobUnitOfMeasure, orderSummary, recurringLoads } = order;
  const deliveredLoadsCount = orderSummary?.deliveredLoadsCount ?? 0;
  const loadsCount = orderSummary?.loadsCount ?? 0;
  const isMaterialCountedInLoads =
    orderSummary?.materialGrossUnitOfMeasure === null ||
    orderSummary?.materialGrossUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD;
  const isDeliveredQuantityEstimated =
    !isMaterialCountedInLoads && orderSummary?.estimatedDeliveredQuantity !== null;

  const delivered = isDeliveredQuantityEstimated
    ? {
        quantity: orderSummary?.estimatedDeliveredQuantity,
        unitOfMeasure: orderSummary?.estimatedDeliveredUnitOfMeasure?.name ?? '',
      }
    : {
        quantity: orderSummary?.deliveredLoadsCount,
        unitOfMeasure: OrderUnitOfMeasure.LOAD,
      };
  const deliveredQuantityText = delivered.quantity
    ? `${formatNumber(delivered.quantity)} ${delivered.unitOfMeasure}`
    : '';

  if (jobUnitOfMeasure?.id !== OrderUnitOfMeasure.LOAD) {
    return {
      deliveredLoadsCount,
      deliveredQuantityText,
      plannedText: makeLoadsText(orderSummary?.calculatedLoadsCount ?? 0),
      plannedTotal: loadsCount,
    };
  }

  if (!recurringLoads) {
    return {
      deliveredLoadsCount,
      deliveredQuantityText,
      plannedText: makeLoadsText(loadsCount),
      plannedTotal: loadsCount,
    };
  }

  const runningQuantity = isNil(jobQuantity) ? 0 : Number(jobQuantity);
  const hasRunningQuantity = runningQuantity > 1;

  return {
    deliveredLoadsCount,
    deliveredQuantityText,
    plannedText: hasRunningQuantity
      ? `${t('common.running')} ${runningQuantity}+`
      : t('common.running'),
    plannedTotal: hasRunningQuantity ? runningQuantity : 0,
  };
}

export function getOrderTicketsSummary(order: Order) {
  const ticketedQuantity = order.orderSummary?.totalTicketsQuantity ?? 0;
  const materialUnitOfMeasure = order.orderSummary?.materialGrossUnitOfMeasure?.name;

  return {
    ticketedQuantityText:
      ticketedQuantity && materialUnitOfMeasure
        ? `${formatNumber(ticketedQuantity)} ${materialUnitOfMeasure}`
        : null,
    ticketedQuantity,
    ticketsCount: order.orderSummary?.totalTicketsCount ?? 0,
  };
}

interface OrderMessagingTabRequest {
  isExpanded: boolean;
  onMessagingTabRequest: () => void;
  onToggleExpanded: () => void;
}

export function openOrderMessagingTab({
  isExpanded,
  onMessagingTabRequest,
  onToggleExpanded,
}: OrderMessagingTabRequest) {
  onMessagingTabRequest();

  if (!isExpanded) {
    onToggleExpanded();
  }
}

interface OrderMaterialRow {
  id: string;
  // Null when the quantity has no material, which horizon-api still emits as a row.
  materialName: Nullable<string>;
  label: string;
  quantity: string;
  unitOfMeasure: string | null;
}

function makeMaterialAndUnitKey(
  materialName: Nullable<string>,
  unitOfMeasure: Nullable<string>,
) {
  return `${materialName ?? ''}::${unitOfMeasure ?? ''}`;
}

function makeMaterialRowLabel(materialName: Nullable<string>) {
  return materialName ?? t('dispatch.order.metrics.unspecified_material');
}

// Mirrors horizon-api's OrderSummary#total_quantities: the order's own material and
// quantity sit beside the additive material totals, and become a separate first row
// only when the order has a quantity and no total already covers its material and unit.
// A quantity with no material is still a row, with no material name.
export function getOrderMaterialRows(order: Order): OrderMaterialRow[] {
  const totalsRows = (order.materialTotals ?? []).map((materialTotal) => {
    const materialName = materialTotal.material?.name ?? null;

    return {
      id: materialTotal.id,
      materialName,
      label: makeMaterialRowLabel(materialName),
      quantity: materialTotal.totalQuantity,
      unitOfMeasure: materialTotal.unitOfMeasure?.id ?? null,
    };
  });

  const orderHasQuantity = !isNil(order.quantity) && String(order.quantity) !== '';
  const totalsAlreadyHaveOrderMaterial =
    order.material !== null &&
    (order.materialTotals ?? []).some(
      (materialTotal) =>
        materialTotal.material?.id === order.material?.id &&
        (materialTotal.unitOfMeasure?.id ?? null) === (order.unitOfMeasure?.id ?? null),
    );

  if (!orderHasQuantity || totalsAlreadyHaveOrderMaterial) {
    return totalsRows;
  }

  const materialName = order.material?.name ?? null;
  const orderRow: OrderMaterialRow = {
    id: `order-${order.material?.id ?? 'unspecified'}`,
    materialName,
    label: makeMaterialRowLabel(materialName),
    quantity: String(order.quantity),
    unitOfMeasure: order.unitOfMeasure?.id ?? null,
  };

  return [orderRow, ...totalsRows];
}

// A delivered figure is per material AND per unit, and ticketed and unticketed loads
// group separately, so one material and unit can arrive as several entries that sum.
// The wire format carries no material id, so rows join by material name. A row with no
// material name reads every delivery in its unit, as horizon-api does, so each unit
// also gets one total over all materials, including loads with no material.
export function getDeliveredByMaterialAndUnit(order: Order) {
  const totals = new Map<string, number>();
  const addDelivered = (key: string, delivered: number) =>
    totals.set(key, (totals.get(key) ?? 0) + delivered);

  for (const deliveredQuantity of order.orderSummary?.deliveredQuantities ?? []) {
    const delivered = Number(deliveredQuantity.delivered ?? 0);

    addDelivered(
      makeMaterialAndUnitKey(null, deliveredQuantity.unitOfMeasure),
      delivered,
    );

    if (deliveredQuantity.materialName) {
      addDelivered(
        makeMaterialAndUnitKey(
          deliveredQuantity.materialName,
          deliveredQuantity.unitOfMeasure,
        ),
        delivered,
      );
    }
  }

  return totals;
}

export function getDeliveredForMaterialRow(
  deliveredByMaterialAndUnit: Map<string, number>,
  row: OrderMaterialRow,
) {
  return (
    deliveredByMaterialAndUnit.get(
      makeMaterialAndUnitKey(row.materialName, row.unitOfMeasure),
    ) ?? null
  );
}

// An order the viewing company cannot edit keeps the single-material widget whatever
// its material count: that widget masks the ordered quantity, and the per-material
// list has no such masking.
export function orderShowsMaterialList(order: Order) {
  return order.editable && getOrderMaterialRows(order).length > 1;
}

// A material with no load carrying it has no delivered figure at all, which differs
// from one whose loads have delivered nothing yet, so it shows an em dash rather than
// a zero that would read as "started but behind".
export function formatMaterialRowLabels(
  delivered: Nullable<number>,
  quantity: string,
  unitOfMeasure: Nullable<string>,
) {
  const orderedQuantity = parseFiniteNumber(quantity);
  const ordered = isNil(orderedQuantity) ? '-' : formatNumber(orderedQuantity);

  return {
    delivered: isNil(delivered) ? '—' : formatNumber(delivered),
    orderedQuantity,
    orderedWithUnit: unitOfMeasure ? `${ordered} ${startCase(unitOfMeasure)}` : ordered,
  };
}
